import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/drawer';
import { api } from '@/lib/utils';

type CsvImportKind = 'agreements' | 'customers' | 'services' | 'equipment';

const SAMPLE_BY_KIND: Record<CsvImportKind, string> = {
  agreements: `customer_name,customer_email,plan_name,amount,end_date,last_service_date
Jane Doe,jane@example.com,Comfort Plan,300,2026-07-10,2025-01-12`,
  customers: `name,email,phone,company,address
Jane Doe,jane@example.com,555-0100,Acme LLC,100 Main St`,
  services: `customer_name,customer_email,service_date,service_title,amount
Jane Doe,jane@example.com,2025-06-15,AC tune-up,149`,
  equipment: `customer_name,customer_email,equipment_name,type,install_year,expected_life_years
Jane Doe,jane@example.com,Carrier 80% Furnace,furnace,2012,15`,
};

const KIND_LABELS: Record<CsvImportKind, string> = {
  agreements: 'Agreements',
  customers: 'Customers',
  services: 'Service visits',
  equipment: 'Equipment',
};

const FIELD_OPTIONS: Record<CsvImportKind, string[]> = {
  agreements: [
    '',
    'customer_name',
    'customer_email',
    'customer_phone',
    'plan_name',
    'amount',
    'end_date',
    'start_date',
    'last_service_date',
    'last_invoice_date',
    'unpaid_invoice',
    'external_ref',
    'technician_name',
  ],
  customers: ['', 'name', 'email', 'phone', 'company', 'address', 'external_ref', 'notes'],
  services: [
    '',
    'customer_name',
    'customer_email',
    'service_date',
    'service_title',
    'amount',
    'external_ref',
    'technician_name',
    'notes',
  ],
  equipment: [
    '',
    'customer_name',
    'customer_email',
    'equipment_name',
    'type',
    'install_year',
    'expected_life_years',
    'external_ref',
    'notes',
  ],
};

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; message: string }>;
}

export function CsvImportModal({
  open,
  onClose,
  onDone,
  defaultKind = 'agreements',
  allowedKinds,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  defaultKind?: CsvImportKind;
  allowedKinds?: CsvImportKind[];
}) {
  const qc = useQueryClient();
  const kinds = allowedKinds ?? (['agreements', 'customers', 'services', 'equipment'] as CsvImportKind[]);
  const [kind, setKind] = useState<CsvImportKind>(defaultKind);
  const [csv, setCsv] = useState('');
  const [step, setStep] = useState<'paste' | 'preview' | 'done'>('paste');
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [dedupeMode, setDedupeMode] = useState<
    'create_only' | 'update_by_email' | 'update_by_external_ref' | 'skip_existing'
  >('create_only');
  const [preview, setPreview] = useState<{
    headers: string[];
    suggestedMapping: Record<string, string | null>;
    previewRows: Array<{ row: number; valid: boolean; issues: string[] }>;
    validRows: number;
    invalidRows: number;
  } | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const previewPath = `/api/v1/import/${kind}/preview`;
  const importPath = `/api/v1/import/${kind}`;

  const previewMutation = useMutation({
    mutationFn: () =>
      api<NonNullable<typeof preview>>(previewPath, {
        method: 'POST',
        body: JSON.stringify({ csv, mapping: Object.keys(mapping).length ? mapping : undefined }),
      }),
    onSuccess: (res) => {
      setPreview(res);
      setMapping(res.suggestedMapping);
      setStep('preview');
    },
  });

  const importMutation = useMutation({
    mutationFn: () =>
      api<ImportResult>(importPath, {
        method: 'POST',
        body: JSON.stringify({
          csv,
          mapping,
          dedupe_mode: kind === 'services' ? undefined : dedupeMode,
        }),
      }),
    onSuccess: (res) => {
      setResult(res);
      setStep('done');
      if (kind === 'agreements' && (res.created > 0 || res.updated > 0)) {
        void qc.invalidateQueries({ queryKey: ['feedback-prompt'] });
      }
      onDone();
    },
  });

  const reset = () => {
    setCsv('');
    setStep('paste');
    setMapping({});
    setPreview(null);
    setResult(null);
    setDedupeMode('create_only');
    setKind(defaultKind);
    previewMutation.reset();
    importMutation.reset();
  };

  const close = () => {
    reset();
    onClose();
  };

  const onFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result ?? ''));
    reader.readAsText(file);
  };

  const fieldOptions = FIELD_OPTIONS[kind];

  return (
    <Modal
      open={open}
      onClose={close}
      title="Import from spreadsheet"
      description="Paste CSV or upload a .csv file. Map columns on the next step."
    >
      {step === 'done' && result ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-success-subtle px-4 py-3 text-sm text-[#166534]">
            Imported {result.created} new and updated {result.updated} existing {KIND_LABELS[kind].toLowerCase()}.
            {result.skipped > 0 && ` ${result.skipped} row${result.skipped === 1 ? '' : 's'} skipped.`}
          </div>
          {result.errors.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-lg border border-border text-[13px]">
              {result.errors.map((e) => (
                <div key={e.row} className="flex gap-2 border-b border-border px-3 py-1.5 last:border-0">
                  <span className="font-medium text-text-secondary">Row {e.row}</span>
                  <span className="text-text-muted">{e.message}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={close}>Done</Button>
          </div>
        </div>
      ) : step === 'preview' && preview ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-bg-muted px-3 py-2 text-[13px] text-text-secondary">
            {preview.validRows} valid, {preview.invalidRows} with issues (showing first {preview.previewRows.length}).
          </div>
          <div className="max-h-36 space-y-2 overflow-y-auto">
            {preview.headers.map((header) => (
              <div key={header} className="grid grid-cols-2 items-center gap-2 text-[13px]">
                <span className="truncate font-mono text-text-muted">{header}</span>
                <select
                  value={mapping[header] ?? ''}
                  onChange={(e) => setMapping((m) => ({ ...m, [header]: e.target.value || null }))}
                  className="rounded-md border border-border bg-bg-surface px-2 py-1"
                >
                  {fieldOptions.map((opt) => (
                    <option key={opt || 'skip'} value={opt}>
                      {opt ? opt.replace(/_/g, ' ') : '— skip —'}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          {kind !== 'services' && (
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">
                When a row matches an existing record
              </label>
              <select
                value={dedupeMode}
                onChange={(e) => setDedupeMode(e.target.value as typeof dedupeMode)}
                className="w-full rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm"
              >
                <option value="create_only">Always create new records</option>
                <option value="update_by_email">Update by email</option>
                <option value="update_by_external_ref">Update by external reference</option>
                <option value="skip_existing">Skip matching rows</option>
              </select>
            </div>
          )}
          {importMutation.isError && (
            <p className="text-[13px] text-danger">{(importMutation.error as Error).message}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setStep('paste')}>
              Back
            </Button>
            <Button onClick={() => importMutation.mutate()} loading={importMutation.isPending}>
              Import {preview.validRows} row{preview.validRows === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {kinds.length > 1 && (
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-text-secondary">Import type</label>
              <select
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as CsvImportKind);
                  setCsv('');
                }}
                className="w-full rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm"
              >
                {kinds.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-[13px] font-medium text-text-secondary">CSV data</label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4" /> Upload file
              </Button>
            </div>
            <textarea
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              rows={9}
              placeholder={SAMPLE_BY_KIND[kind]}
              className="w-full rounded-lg border border-border bg-bg-surface px-3 py-2 font-mono text-xs leading-relaxed transition-colors placeholder:text-text-muted focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25"
            />
          </div>
          {previewMutation.isError && (
            <p className="text-[13px] text-danger">{(previewMutation.error as Error).message}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button
              onClick={() => previewMutation.mutate()}
              loading={previewMutation.isPending}
              disabled={!csv.trim()}
            >
              Preview &amp; map columns
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
